// ============================================================
// WizardMatriculaPage — Asistente de Matrícula completa
// 4 pasos en una sola pantalla:
//   1. Estudiante (buscar o crear)
//   2. Matrícula (selección + tarifa)
//   3. Pago de matrícula (opcional)
//   4. Documentos (descargas)
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { catalogoService, usuarioService } from 'services/usuarioService';
import { matriculaService } from 'services/matriculaService';
import {
  tarifaService, pagoMatriculaService, estadoCuentaService,
  tipoDescuentoService, descuentoEstudianteService,
} from 'services/pagoService';
import { documentoService } from 'services/documentoService';
import { exportarPDF } from 'utils/reporteExport';
import { htmlConstancia } from 'utils/documentoPdf';
import { fmtAnioEscolar } from 'utils/anioEscolar';
import { AñoEscolar, Grado, Seccion, UsuarioListItem } from 'types/usuario.types';
import { MatriculaResponse } from 'types/matricula.types';
import { TarifaAnual, EstadoCuenta, PagarPayload, TipoDescuento } from 'types/pago.types';
import {
  I, Card, Badge, PageHead, EmptyState,
} from 'components/ui';
import DrawerCrearEstudiante from 'components/wizard/DrawerCrearEstudiante';
import FumPage from 'pages/FumPage';

interface Props { onFinish: () => void; }

type Step = 1 | 2 | 3 | 4 | 5;

const sol = (n: number) =>
  new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(n);

// ════════════════════════════════════════════════════════════
// Stepper visual
// ════════════════════════════════════════════════════════════

const Stepper: React.FC<{ step: Step; conFum: boolean }> = ({ step, conFum }) => {
  // Si el estudiante ya existe, saltamos el paso FUM
  const labels = conFum
    ? ['Estudiante', 'Matrícula', 'Pago', 'FUM', 'Documentos']
    : ['Estudiante', 'Matrícula', 'Pago', 'Documentos'];

  // Cuando NO hay FUM, el "paso 4" lógico es "Documentos" pero internamente es Step 5.
  // Mapeamos el step interno (1-5) al índice visual del array de labels:
  const visualIndex = conFum
    ? step
    : step === 5 ? 4 : step;
  return (
    <div
      className="flex items-center gap-1.5 mb-5 px-1 overflow-x-auto"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {labels.map((label, i) => {
        const num    = i + 1;
        const done   = num < visualIndex;
        const active = num === visualIndex;
        return (
          <React.Fragment key={label}>
            <div
              className="flex items-center gap-2 flex-shrink-0"
              style={{ opacity: !done && !active ? 0.5 : 1 }}
            >
              <div
                className="rounded-full grid place-items-center font-bold text-[12px] flex-shrink-0"
                style={{
                  width: 28, height: 28,
                  background: done   ? 'var(--success)'
                            : active ? 'var(--primary-700)'
                            :          'var(--surface-2)',
                  color:      done || active ? 'white' : 'var(--ink-500)',
                  border:     done || active ? 'none' : '1px solid var(--border-strong)',
                }}
              >
                {done ? <I.Check size={14} /> : num}
              </div>
              <span
                className={`text-[13px] font-semibold whitespace-nowrap ${active ? 'text-ink-900' : 'text-ink-500'}`}
              >
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div
                className="flex-1 h-px min-w-[16px]"
                style={{ background: done ? 'var(--success)' : 'var(--border)' }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ════════════════════════════════════════════════════════════
// Main component
// ════════════════════════════════════════════════════════════

const WizardMatriculaPage: React.FC<Props> = ({ onFinish }) => {
  const { user } = useAuth();
  const PUEDE_CREAR_USUARIO = user?.rol === 'ADMIN' || user?.rol === 'SECRETARIA';

  const [step, setStep] = useState<Step>(1);

  // Catálogos compartidos
  const [anios, setAnios]                     = useState<AñoEscolar[]>([]);
  const [grados, setGrados]                   = useState<Grado[]>([]);
  const [secciones, setSecciones]             = useState<Seccion[]>([]);
  const [tarifas, setTarifas]                 = useState<TarifaAnual[]>([]);
  const [tiposDescuento, setTiposDescuento]   = useState<TipoDescuento[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const [a, g, s, t] = await Promise.all([
          catalogoService.getAñosEscolares(),
          catalogoService.getGrados(),
          catalogoService.getSecciones(),
          tipoDescuentoService.listar(),
        ]);
        setAnios(a);
        setGrados(g);
        setSecciones(s);
        setTiposDescuento(t);
      } catch {}
    })();
  }, []);

  // ── Estado de paso 1: estudiante ──────────────────────────
  const [query, setQuery]                           = useState('');
  const [busquedaResultados, setBusquedaResultados] = useState<UsuarioListItem[]>([]);
  const [buscando, setBuscando]                     = useState(false);
  const [estudiante, setEstudiante]                 = useState<UsuarioListItem | null>(null);
  const [esEstudianteNuevo, setEsEstudianteNuevo]   = useState(false);
  const [drawerCrearAbierto, setDrawerCrear]        = useState(false);

  const buscar = async () => {
    if (!query.trim()) return;
    setBuscando(true);
    try {
      const data = await usuarioService.listarEstudiantes({ search: query });
      setBusquedaResultados(Array.isArray(data) ? data : []);
    } catch {} finally { setBuscando(false); }
  };

  const handleSeleccionarEstudiante = (u: UsuarioListItem) => {
    setEstudiante(u);
    setEsEstudianteNuevo(false);   // estudiante existente
    setBusquedaResultados([]);
  };

  const handleEstudianteCreado = (u: UsuarioListItem) => {
    setEstudiante(u);
    setEsEstudianteNuevo(true);    // estudiante recién creado → necesita FUM
    setDrawerCrear(false);
    setBusquedaResultados([]);
  };

  // ── Estado de paso 2: matrícula ──────────────────────────
  const [anioId, setAnioId]                         = useState<number | ''>('');
  const [nivel, setNivel]                           = useState('');
  const [gradoId, setGradoId]                       = useState<number | ''>('');
  const [seccionId, setSeccionId]                   = useState<number | ''>('');
  const [tarifaAplicable, setTarifaAplicable]       = useState<TarifaAnual | null>(null);
  const [creandoMatricula, setCreandoMatricula]     = useState(false);
  const [errorMatricula, setErrorMatricula]         = useState<string | null>(null);
  const [matriculaCreada, setMatriculaCreada]       = useState<MatriculaResponse | null>(null);
  // Descuentos seleccionados manualmente para asignar al matricular
  const [descuentosSeleccionados, setDescuentosSeleccionados] = useState<Set<number>>(new Set());

  const toggleDescuento = (idTipo: number) => {
    setDescuentosSeleccionados(prev => {
      const next = new Set(prev);
      if (next.has(idTipo)) next.delete(idTipo);
      else next.add(idTipo);
      return next;
    });
  };

  const niveles = useMemo(
    () => Array.from(new Set(grados.map(g => g.nivel))).sort(),
    [grados],
  );
  const gradosFiltrados = useMemo(
    () => nivel ? grados.filter(g => g.nivel === nivel) : grados,
    [grados, nivel],
  );
  const seccionesFiltradas = useMemo(() => {
    if (!gradoId) return secciones;
    return secciones.filter(s => s.gradoId === Number(gradoId));
  }, [secciones, gradoId]);

  // Carga tarifas cuando cambia el año
  useEffect(() => {
    if (!anioId) { setTarifas([]); return; }
    tarifaService.listar(Number(anioId)).then(setTarifas).catch(() => setTarifas([]));
  }, [anioId]);

  // Calcula la tarifa del nivel del grado seleccionado
  useEffect(() => {
    if (!gradoId || tarifas.length === 0) {
      setTarifaAplicable(null);
      return;
    }
    const grado  = grados.find(g => g.idGrado === Number(gradoId));
    if (!grado) { setTarifaAplicable(null); return; }
    const tarifa = tarifas.find(t => t.nivel === grado.nivel);
    setTarifaAplicable(tarifa ?? null);
  }, [gradoId, tarifas, grados]);

  const anioSeleccionado = anios.find(a => a.idAnioEscolar === anioId) ?? null;
  const esRecuperacion   = anioSeleccionado?.fase === 'RECUPERACION';

  const handleConfirmarMatricula = async () => {
    if (!estudiante || !anioId || !gradoId || !seccionId) return;
    setCreandoMatricula(true);
    setErrorMatricula(null);
    try {
      const m = await matriculaService.crear({
        estudianteId:  estudiante.idUsuario,
        gradoId:       Number(gradoId),
        seccionId:     Number(seccionId),
        anioEscolarId: Number(anioId),
      });
      // Asignar los descuentos seleccionados a la matrícula recién creada
      // (en serie para no saturar el backend; son pocos típicamente 1-3)
      for (const idTipoDescuento of Array.from(descuentosSeleccionados)) {
        try {
          await descuentoEstudianteService.asignar({
            matriculaId:     m.idMatricula,
            tipoDescuentoId: idTipoDescuento,
          });
        } catch {
          // Si un descuento falla, lo ignoramos silenciosamente — el resto
          // del flujo sigue. Se puede ajustar después desde el módulo de Cobros.
        }
      }
      setMatriculaCreada(m);
      setStep(3);
    } catch (e: unknown) {
      setErrorMatricula(e instanceof Error ? e.message : 'Error al crear matrícula');
    } finally {
      setCreandoMatricula(false);
    }
  };

  // ── Estado de paso 3: pago ──────────────────────────────
  const [estadoCuenta, setEstadoCuenta]   = useState<EstadoCuenta | null>(null);
  const [observacion, setObservacion]     = useState('');
  const [cobrando, setCobrando]           = useState(false);
  const [errorCobro, setErrorCobro]       = useState<string | null>(null);
  const [pagoCompletado, setPagoCompletado] = useState(false);
  const idempotencyKeyRef                   = React.useRef('');

  // Cargar estado de cuenta al entrar al paso 3
  useEffect(() => {
    if (step !== 3 || !estudiante || !anioId) return;
    estadoCuentaService.getByEstudiante(estudiante.idUsuario, Number(anioId))
      .then(c => setEstadoCuenta(c))
      .catch(() => setEstadoCuenta(null));
    idempotencyKeyRef.current = crypto.randomUUID();
  }, [step, estudiante, anioId]);

  const pagoMatricula = estadoCuenta?.pagoMatricula;

  const handleCobrar = async () => {
    if (!estudiante || !anioId || !pagoMatricula) return;
    setCobrando(true);
    setErrorCobro(null);
    try {
      const payload: PagarPayload = {
        tipoDescuentoId:    null,
        montoPersonalizado: null,
        registradoPorId:    user?.idUsuario ?? 0,
        observacion:        observacion || undefined,
      };
      await pagoMatriculaService.pagar(
        estudiante.idUsuario,
        Number(anioId),
        payload,
        idempotencyKeyRef.current,
      );
      setPagoCompletado(true);
      // Si es estudiante nuevo va al paso FUM, si es existente salta a Documentos
      setStep(esEstudianteNuevo ? 4 : 5);
    } catch (e: unknown) {
      setErrorCobro(e instanceof Error ? e.message : 'Error al registrar el pago');
    } finally {
      setCobrando(false);
    }
  };

  const handleSaltarPago = () => {
    setPagoCompletado(false);
    setStep(esEstudianteNuevo ? 4 : 5);
  };

  // ── Estado de paso 4: documentos ────────────────────────
  const [descargandoDoc, setDescargandoDoc] = useState<string | null>(null);
  const [errorDoc, setErrorDoc]             = useState<string | null>(null);

  const descargarConstancia = async () => {
    if (!matriculaCreada) return;
    setDescargandoDoc('constancia');
    setErrorDoc(null);
    try {
      const data = await documentoService.getConstancia(matriculaCreada.idMatricula);
      exportarPDF(
        `Constancia de Matrícula — ${matriculaCreada.estudianteApellidos} ${matriculaCreada.estudianteNombres}`,
        htmlConstancia(data),
      );
    } catch (e: unknown) {
      setErrorDoc(e instanceof Error ? e.message : 'Error al generar constancia');
    } finally {
      setDescargandoDoc(null);
    }
  };

  const reset = () => {
    setStep(1);
    setQuery('');
    setBusquedaResultados([]);
    setEstudiante(null);
    setEsEstudianteNuevo(false);
    setAnioId(''); setNivel(''); setGradoId(''); setSeccionId('');
    setTarifaAplicable(null);
    setDescuentosSeleccionados(new Set());
    setMatriculaCreada(null);
    setEstadoCuenta(null);
    setObservacion('');
    setPagoCompletado(false);
    setErrorMatricula(null);
    setErrorCobro(null);
    setErrorDoc(null);
  };

  // ════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════

  return (
    <>
      <PageHead
        title="Asistente de Matrícula"
        sub={
          esEstudianteNuevo
            ? 'Estudiante · Matrícula · Pago · FUM · Documentos'
            : 'Estudiante · Matrícula · Pago · Documentos'
        }
        actions={
          step > 1 && step < 5 ? (
            <button onClick={onFinish} className="btn btn-ghost">
              <I.X size={14} /> Salir del asistente
            </button>
          ) : null
        }
      />

      <Stepper step={step} conFum={esEstudianteNuevo} />

      {/* ═══════════ PASO 1: ESTUDIANTE ═══════════ */}
      {step === 1 && (
        <Card title="¿Quién se va a matricular?" sub="Busca un estudiante existente o crea uno nuevo">
          {estudiante ? (
            // Estudiante seleccionado
            <div>
              <div
                className="row p-4 rounded-r-lg border-2 mb-4"
                style={{
                  background: 'var(--primary-50)',
                  borderColor: 'var(--primary-300)',
                  gap: 14,
                }}
              >
                <div
                  className="rounded-full grid place-items-center text-white font-bold flex-shrink-0"
                  style={{
                    width: 48, height: 48,
                    background: 'linear-gradient(135deg, var(--primary-500), var(--accent-500))',
                  }}
                >
                  {estudiante.nombres.charAt(0)}{estudiante.apellidos.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="row mb-1" style={{ gap: 8 }}>
                    <p className="text-[15px] font-bold text-ink-900 m-0 truncate">
                      {estudiante.apellidos}, {estudiante.nombres}
                    </p>
                    <Badge kind="success" dot>Seleccionado</Badge>
                  </div>
                  <p className="text-[12px] text-ink-500 m-0">
                    DNI {estudiante.dni}
                    {estudiante.email && <> · {estudiante.email}</>}
                  </p>
                </div>
                <button
                  onClick={() => setEstudiante(null)}
                  className="btn btn-ghost btn-sm flex-shrink-0"
                  title="Cambiar estudiante"
                >
                  <I.Edit size={13} /> Cambiar
                </button>
              </div>

              <div className="flex justify-end">
                <button onClick={() => setStep(2)} className="btn btn-primary">
                  Siguiente <I.ChevronRight size={14} />
                </button>
              </div>
            </div>
          ) : (
            // Buscar / crear
            <div>
              <div className="field">
                <label>Buscar estudiante existente</label>
                <div className="flex gap-2">
                  <input
                    className="input"
                    placeholder="DNI, nombres o apellidos…"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && buscar()}
                  />
                  <button
                    onClick={buscar}
                    disabled={buscando || !query.trim()}
                    className="btn btn-secondary flex-shrink-0"
                  >
                    {buscando ? <I.Activity size={14} className="animate-pulse" /> : <I.Search size={14} />}
                    Buscar
                  </button>
                </div>
              </div>

              {/* Resultados */}
              {busquedaResultados.length > 0 && (
                <div className="mt-3 grid gap-1.5">
                  {busquedaResultados.map(u => (
                    <button
                      key={u.idUsuario}
                      onClick={() => handleSeleccionarEstudiante(u)}
                      className="row p-3 rounded-r-sm border bg-white hover:bg-surface-alt transition-colors text-left"
                      style={{ borderColor: 'var(--border)', gap: 12 }}
                    >
                      <div
                        className="rounded-full grid place-items-center text-white font-bold text-[12px] flex-shrink-0"
                        style={{
                          width: 36, height: 36,
                          background: 'linear-gradient(135deg, var(--primary-500), var(--accent-500))',
                        }}
                      >
                        {u.nombres.charAt(0)}{u.apellidos.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-ink-900 m-0 truncate">
                          {u.apellidos}, {u.nombres}
                        </p>
                        <p className="text-[11px] text-ink-500 m-0">DNI {u.dni}</p>
                      </div>
                      <I.ChevronRight size={14} className="text-ink-400" />
                    </button>
                  ))}
                </div>
              )}

              {busquedaResultados.length === 0 && query && !buscando && (
                <div className="mt-3 text-center text-[13px] text-ink-500 py-4">
                  Sin resultados para "{query}"
                </div>
              )}

              {/* Separador y crear nuevo */}
              {PUEDE_CREAR_USUARIO && (
                <>
                  <div className="flex items-center gap-3 my-5">
                    <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
                    <span className="text-[11px] text-ink-400 uppercase tracking-wider font-semibold">
                      o crear uno nuevo
                    </span>
                    <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
                  </div>
                  <button
                    onClick={() => setDrawerCrear(true)}
                    className="btn btn-secondary w-full justify-center py-3"
                  >
                    <I.Plus size={16} /> Crear estudiante nuevo
                  </button>
                </>
              )}
            </div>
          )}
        </Card>
      )}

      {/* ═══════════ PASO 2: MATRÍCULA ═══════════ */}
      {step === 2 && estudiante && (
        <div className="grid-12">
          <Card title="Datos de la matrícula" sub={`Para ${estudiante.apellidos}, ${estudiante.nombres}`}>
            <div className="grid gap-4">
              <div className="field">
                <label>Año escolar <span className="required">*</span></label>
                <select
                  className="select"
                  value={anioId}
                  onChange={e => {
                    setAnioId(e.target.value === '' ? '' : Number(e.target.value));
                    setNivel(''); setGradoId(''); setSeccionId('');
                  }}
                >
                  <option value="">Seleccionar año</option>
                  {anios.filter(a => a.activo).map(a => (
                    <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
                  ))}
                </select>
                {anios.filter(a => a.activo).length === 0 && (
                  <p className="text-[11px] text-amber-700">
                    No hay años escolares activos. Solicita al admin aperturar uno en Configuración.
                  </p>
                )}
                {esRecuperacion && (
                  <p className="text-[11px] text-amber-700 font-semibold">
                    ⚠ Año de Recuperación — solo se cobra matrícula, sin pensiones mensuales
                  </p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="field">
                  <label>Nivel <span className="required">*</span></label>
                  <select
                    className="select"
                    value={nivel}
                    onChange={e => { setNivel(e.target.value); setGradoId(''); setSeccionId(''); }}
                    disabled={!anioId}
                  >
                    <option value="">—</option>
                    {niveles.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Grado <span className="required">*</span></label>
                  <select
                    className="select"
                    value={gradoId}
                    onChange={e => { setGradoId(e.target.value === '' ? '' : Number(e.target.value)); setSeccionId(''); }}
                    disabled={!nivel}
                  >
                    <option value="">—</option>
                    {gradosFiltrados.map(g => <option key={g.idGrado} value={g.idGrado}>{g.nombre}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Sección <span className="required">*</span></label>
                  <select
                    className="select"
                    value={seccionId}
                    onChange={e => setSeccionId(e.target.value === '' ? '' : Number(e.target.value))}
                    disabled={!gradoId}
                  >
                    <option value="">—</option>
                    {seccionesFiltradas.map(s => <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>)}
                  </select>
                </div>
              </div>

              {errorMatricula && (
                <div
                  className="rounded-r-sm border border-red-200 px-3 py-2.5 text-[13px]"
                  style={{ background: 'var(--danger-bg)', color: '#991b1b' }}
                >
                  <strong>Error:</strong> {errorMatricula}
                </div>
              )}

              <div className="flex justify-between gap-2 pt-2">
                <button onClick={() => setStep(1)} className="btn btn-ghost">
                  <I.ChevronLeft size={14} /> Atrás
                </button>
                <button
                  onClick={handleConfirmarMatricula}
                  disabled={creandoMatricula || !anioId || !gradoId || !seccionId || !tarifaAplicable}
                  className="btn btn-primary"
                >
                  {creandoMatricula
                    ? <I.Activity size={14} className="animate-pulse" />
                    : <I.Check size={14} />}
                  Confirmar matrícula
                </button>
              </div>
            </div>
          </Card>

          <Card title="Resumen de costos">
            {!gradoId && (
              <p className="text-center muted text-[13px] py-4 m-0">
                Selecciona el grado para ver la tarifa
              </p>
            )}
            {gradoId && !tarifaAplicable && (
              <div
                className="rounded-r-sm p-3"
                style={{ background: 'var(--danger-bg)', color: '#991b1b' }}
              >
                <p className="text-[13px] font-bold m-0">Sin tarifa configurada</p>
                <p className="text-[11px] m-0 mt-1">
                  No hay tarifa para este nivel en el año seleccionado.
                  Configúrala antes de matricular.
                </p>
              </div>
            )}
            {tarifaAplicable && (
              <div className="grid gap-2.5 text-[13px]">
                <div className="row between">
                  <span className="muted">Matrícula</span>
                  <b className="tabular">{sol(tarifaAplicable.costoMatricula)}</b>
                </div>
                {!esRecuperacion && (
                  <>
                    <div className="row between">
                      <span className="muted">Pensión mensual</span>
                      <b className="tabular">{sol(tarifaAplicable.costoPension)}</b>
                    </div>
                    <div className="row between">
                      <span className="muted">N° de pensiones</span>
                      <b className="tabular">{tarifaAplicable.numPensiones}</b>
                    </div>
                    <div className="divider" />
                    <div className="row between">
                      <span className="text-ink-700 font-semibold">Total anual</span>
                      <b className="tabular text-primary-700">
                        {sol(tarifaAplicable.costoMatricula + tarifaAplicable.costoPension * tarifaAplicable.numPensiones)}
                      </b>
                    </div>
                  </>
                )}
                <div className="rounded-r-sm p-2.5 mt-2" style={{ background: 'var(--info-bg)' }}>
                  <p className="text-[11.5px] m-0" style={{ color: '#1e40af' }}>
                    {esRecuperacion
                      ? 'Solo se generará el cobro de matrícula.'
                      : `Se generará el cobro de matrícula y ${tarifaAplicable.numPensiones} pensiones pendientes.`}
                  </p>
                </div>

                {/* Descuentos disponibles para aplicar */}
                {tiposDescuento.filter(t => t.activo && t.aplicaMatricula).length > 0 && (
                  <div className="mt-2">
                    <div className="divider" />
                    <p
                      className="text-[11px] font-bold uppercase tracking-wide mb-2 m-0"
                      style={{ color: 'var(--accent-700)' }}
                    >
                      Descuentos disponibles ({descuentosSeleccionados.size} seleccionados)
                    </p>
                    <div className="grid gap-1.5">
                      {tiposDescuento
                        .filter(t => t.activo && t.aplicaMatricula)
                        .map(t => {
                          const seleccionado = descuentosSeleccionados.has(t.idTipoDescuento);
                          return (
                            <button
                              key={t.idTipoDescuento}
                              type="button"
                              onClick={() => toggleDescuento(t.idTipoDescuento)}
                              className="row text-left p-2 rounded-r-sm border transition-all"
                              style={{
                                gap: 8,
                                background: seleccionado ? 'var(--accent-50)' : 'white',
                                borderColor: seleccionado ? 'var(--accent-500)' : 'var(--border-strong)',
                              }}
                            >
                              <div
                                className="rounded-r-sm grid place-items-center flex-shrink-0"
                                style={{
                                  width: 18, height: 18,
                                  border: '2px solid',
                                  borderColor: seleccionado ? 'var(--accent-700)' : 'var(--border-strong)',
                                  background: seleccionado ? 'var(--accent-500)' : 'white',
                                }}
                              >
                                {seleccionado && <I.Check size={11} style={{ color: 'white' }} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="text-[12.5px] font-semibold text-ink-900">{t.nombre}</span>
                                {t.aplicaPension && (
                                  <span className="ml-1.5 text-[10px] text-ink-500">
                                    (también pensiones)
                                  </span>
                                )}
                              </div>
                              <span
                                className="tabular text-[12.5px] font-bold flex-shrink-0"
                                style={{ color: 'var(--accent-700)' }}
                              >
                                {t.tipo === 'PORCENTAJE' ? `${t.valor}%` : `-${sol(t.valor)}`}
                              </span>
                            </button>
                          );
                        })}
                    </div>
                    <p className="help-text mt-1.5">
                      Marca los descuentos que aplican al estudiante. Se asignarán al confirmar la matrícula.
                    </p>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══════════ PASO 3: PAGO ═══════════ */}
      {step === 3 && matriculaCreada && (
        <Card
          title="Pago de matrícula"
          sub={`Matrícula N° ${matriculaCreada.idMatricula} creada exitosamente`}
          action={<Badge kind="success" dot>Matrícula registrada</Badge>}
        >
          {!estadoCuenta && (
            <div className="text-center py-8 muted">
              <I.Activity size={28} className="animate-pulse mx-auto mb-2 text-ink-300" />
              <p className="text-[13px] m-0">Cargando estado de cuenta…</p>
            </div>
          )}

          {estadoCuenta && pagoMatricula && (
            <div className="grid gap-4">
              {/* Descuentos asignados al estudiante */}
              {estadoCuenta.descuentos.length > 0 ? (
                <div
                  className="rounded-r-lg border p-3.5"
                  style={{ borderColor: 'var(--accent-100)', background: 'var(--accent-50)' }}
                >
                  <p
                    className="text-[11px] font-bold uppercase tracking-wide mb-2 m-0"
                    style={{ color: 'var(--accent-700)' }}
                  >
                    Descuentos del estudiante
                    <span className="ml-1.5 text-ink-500 font-normal normal-case">
                      ({estadoCuenta.descuentos.length})
                    </span>
                  </p>
                  <div className="grid gap-1.5">
                    {estadoCuenta.descuentos.map(d => (
                      <div key={d.idTipoDescuento} className="row between text-[13px]">
                        <div className="row" style={{ gap: 8 }}>
                          <I.Wallet size={14} style={{ color: 'var(--accent-700)' }} />
                          <span className="text-ink-900 font-semibold">{d.nombre}</span>
                          {d.aplicaMatricula && <Badge kind="teal">Matrícula</Badge>}
                          {d.aplicaPension   && <Badge kind="indigo">Pensiones</Badge>}
                        </div>
                        <span className="tabular font-bold" style={{ color: 'var(--accent-700)' }}>
                          {d.tipo === 'PORCENTAJE' ? `${d.valor}%` : `-${sol(d.valor)}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  className="rounded-r-sm p-2.5 text-[12px]"
                  style={{ background: 'var(--surface-2)', color: 'var(--ink-500)' }}
                >
                  Este estudiante no tiene descuentos asignados — se cobra la tarifa completa.
                </div>
              )}

              {/* Resumen del cobro */}
              <div
                className="rounded-r-lg border p-4"
                style={{ borderColor: 'var(--primary-200)', background: 'var(--primary-50)' }}
              >
                <p className="text-[11px] font-bold uppercase tracking-wide mb-2 m-0" style={{ color: 'var(--primary-700)' }}>
                  Pago de matrícula a registrar
                </p>
                <div className="grid gap-1.5 text-[13px]">
                  <div className="row between">
                    <span className="muted">Tarifa base</span>
                    <span className="tabular">{sol(pagoMatricula.montoTarifa)}</span>
                  </div>
                  {pagoMatricula.montoDescuento > 0 && (
                    <div className="row between">
                      <span className="muted">
                        Descuento aplicado
                        {pagoMatricula.descuentoNombre && (
                          <span className="ml-1 text-ink-400 font-normal">
                            · {pagoMatricula.descuentoNombre}
                          </span>
                        )}
                      </span>
                      <span className="tabular" style={{ color: 'var(--accent-700)' }}>
                        -{sol(pagoMatricula.montoDescuento)}
                      </span>
                    </div>
                  )}
                  <div className="divider" style={{ margin: '4px 0' }} />
                  <div className="row between">
                    <span className="font-semibold text-ink-900">Total a pagar</span>
                    <b className="text-[18px] tabular" style={{ color: 'var(--primary-700)' }}>
                      {sol(pagoMatricula.montoFinal)}
                    </b>
                  </div>
                </div>
              </div>

              <div className="field">
                <label>Observación (opcional)</label>
                <textarea
                  className="textarea"
                  rows={2}
                  value={observacion}
                  onChange={e => setObservacion(e.target.value)}
                  placeholder="Ej: pago en efectivo en caja"
                />
              </div>

              {errorCobro && (
                <div
                  className="rounded-r-sm border border-red-200 px-3 py-2.5 text-[13px]"
                  style={{ background: 'var(--danger-bg)', color: '#991b1b' }}
                >
                  <strong>Error:</strong> {errorCobro}
                </div>
              )}

              <div className="flex justify-between flex-wrap gap-2 pt-2">
                <button onClick={handleSaltarPago} className="btn btn-ghost">
                  Saltar y cobrar después →
                </button>
                <button
                  onClick={handleCobrar}
                  disabled={cobrando}
                  className="btn btn-primary"
                  style={{ background: 'var(--success)' }}
                >
                  {cobrando
                    ? <I.Activity size={14} className="animate-pulse" />
                    : <I.DollarSign size={14} />}
                  Cobrar matrícula ({sol(pagoMatricula.montoFinal)})
                </button>
              </div>
            </div>
          )}

          {estadoCuenta && !pagoMatricula && (
            <div>
              <EmptyState
                icon="CheckCircle"
                title="No hay pago de matrícula pendiente"
                sub="La matrícula no generó un cargo (revisa la tarifa)"
              />
              <div className="flex justify-end mt-3">
                <button onClick={() => setStep(esEstudianteNuevo ? 4 : 5)} className="btn btn-primary">
                  Continuar <I.ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* ═══════════ PASO 4: FUM (opcional) ═══════════ */}
      {step === 4 && matriculaCreada && (
        <Card
          title="Ficha Única de Matrícula (FUM)"
          sub="Registra tutores/apoderados y datos de salud del estudiante"
          action={<Badge kind="warning">Opcional pero recomendada</Badge>}
        >
          <div
            className="rounded-r-lg p-3.5 mb-4 flex gap-3 items-start"
            style={{ background: 'var(--info-bg)', border: '1px solid #93c5fd' }}
          >
            <I.AlertCircle size={18} style={{ color: 'var(--info)', flexShrink: 0, marginTop: 2 }} />
            <div className="text-[12.5px]" style={{ color: '#1e40af' }}>
              <p className="font-semibold m-0">¿Por qué llenar la FUM ahora?</p>
              <p className="m-0 mt-1">
                Sin FUM completa, algunos documentos como la <strong>boleta</strong>, <strong>certificado</strong> y la
                <strong> nómina UGEL</strong> pueden bloquearse. Puedes saltar este paso y completarla después
                desde el listado de matrículas.
              </p>
            </div>
          </div>

          {/* FUM embebida */}
          <FumPage
            matriculaId={matriculaCreada.idMatricula}
            estudianteNombre={`${matriculaCreada.estudianteApellidos}, ${matriculaCreada.estudianteNombres}`}
            onBack={() => {}}
            embedded
          />

          <div className="divider" />

          <div className="flex justify-between gap-2 flex-wrap">
            <button onClick={() => setStep(5)} className="btn btn-ghost">
              Saltar y continuar →
            </button>
            <button onClick={() => setStep(5)} className="btn btn-primary">
              <I.Check size={14} /> Continuar a documentos
            </button>
          </div>
        </Card>
      )}

      {/* ═══════════ PASO 5: DOCUMENTOS ═══════════ */}
      {step === 5 && matriculaCreada && (
        <>
          <Card
            title="¡Matrícula completada!"
            sub={`${matriculaCreada.estudianteApellidos}, ${matriculaCreada.estudianteNombres} · ${matriculaCreada.gradoNombre} ${matriculaCreada.seccionNombre}`}
            action={
              pagoCompletado
                ? <Badge kind="success" dot>Matrícula pagada</Badge>
                : <Badge kind="warning" dot>Matrícula sin pago</Badge>
            }
          >
            <div
              className="row p-4 rounded-r-lg border mb-4"
              style={{ background: 'var(--success-bg)', borderColor: '#86efac', gap: 14 }}
            >
              <div
                className="rounded-full grid place-items-center text-white flex-shrink-0"
                style={{ width: 48, height: 48, background: 'var(--success)' }}
              >
                <I.Check size={26} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-bold m-0" style={{ color: '#15803d' }}>
                  Proceso finalizado
                </p>
                <p className="text-[12px] m-0 mt-0.5" style={{ color: '#166534' }}>
                  La matrícula N° {matriculaCreada.idMatricula} fue creada
                  {pagoCompletado ? ' y el pago se registró correctamente.' : '. El pago queda pendiente.'}
                </p>
              </div>
            </div>

            <p className="text-[13px] font-bold text-ink-900 mb-2 m-0">
              Documentos disponibles
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <button
                onClick={descargarConstancia}
                disabled={descargandoDoc !== null}
                className="flex flex-col items-center gap-2 p-4 rounded-r-lg border bg-white hover:shadow-soft transition-all"
                style={{ borderColor: 'var(--border)' }}
              >
                <div
                  className="rounded-r-sm grid place-items-center"
                  style={{ width: 44, height: 44, background: 'var(--primary-100)', color: 'var(--primary-700)' }}
                >
                  {descargandoDoc === 'constancia'
                    ? <I.Activity size={22} className="animate-pulse" />
                    : <I.FileText size={22} />}
                </div>
                <span className="text-[12.5px] font-semibold text-ink-900 text-center">Constancia de matrícula</span>
              </button>

              {/* Stubs para más documentos a futuro */}
              <button
                disabled
                className="flex flex-col items-center gap-2 p-4 rounded-r-lg border bg-white opacity-40 cursor-not-allowed"
                style={{ borderColor: 'var(--border)' }}
                title="Próximamente"
              >
                <div
                  className="rounded-r-sm grid place-items-center"
                  style={{ width: 44, height: 44, background: '#fef3c7', color: '#92400e' }}
                >
                  <I.Receipt size={22} />
                </div>
                <span className="text-[12.5px] font-semibold text-ink-500 text-center">
                  Boleta de pago
                  <span className="block text-[10px] muted font-normal">Próximamente</span>
                </span>
              </button>

              <button
                disabled
                className="flex flex-col items-center gap-2 p-4 rounded-r-lg border bg-white opacity-40 cursor-not-allowed"
                style={{ borderColor: 'var(--border)' }}
                title="Próximamente"
              >
                <div
                  className="rounded-r-sm grid place-items-center"
                  style={{ width: 44, height: 44, background: 'var(--accent-100)', color: 'var(--accent-700)' }}
                >
                  <I.User size={22} />
                </div>
                <span className="text-[12.5px] font-semibold text-ink-500 text-center">
                  Carnet
                  <span className="block text-[10px] muted font-normal">Próximamente</span>
                </span>
              </button>
            </div>

            {errorDoc && (
              <div
                className="rounded-r-sm border border-red-200 px-3 py-2.5 text-[13px] mt-3"
                style={{ background: 'var(--danger-bg)', color: '#991b1b' }}
              >
                <strong>Error al descargar:</strong> {errorDoc}
              </div>
            )}

            <div className="divider" />

            <div className="flex justify-between gap-2 flex-wrap">
              <button onClick={onFinish} className="btn btn-secondary">
                <I.Check size={14} /> Finalizar y volver al listado
              </button>
              <button onClick={reset} className="btn btn-primary">
                <I.Plus size={14} /> Matricular otro estudiante
              </button>
            </div>
          </Card>
        </>
      )}

      {/* Drawer crear estudiante */}
      <DrawerCrearEstudiante
        open={drawerCrearAbierto}
        onClose={() => setDrawerCrear(false)}
        onCreated={handleEstudianteCreado}
        initialQuery={query}
      />
    </>
  );
};

export default WizardMatriculaPage;
